import * as d3 from 'd3';
import React from 'react';

import asm from 'assembly/Asm';
import {Sequence} from 'components/Sequence';
import {getTickColor, getTicks} from 'molbio/maps/circular/CircularAxisUtil';
import {AxisLayout} from 'molbio/maps/circular/CircularLayout';
import Display from 'molbio/maps/circular/Display';
import * as sequtil from 'molbio/SeqUtil';
import {getBaseWidthStripeParams} from 'molbio/visualization/FragmentVisualizationUtil';

interface CircularAxisProps {
  /**
   * A function mapping from base pair index to the angle at which ticks at that base pair should be drawn
   * on screen
   */
  basePairToAngle: (basePair: number) => number;
  /** Whether the entire plasmid is visible */
  fullCircleView: boolean;
  /** The AxisLayout instance for this circular map */
  layout: AxisLayout;
  /** The leftmost base pair shown in the viewport */
  leftBP: number;
  /** The rightmost base pair shown in the viewport */
  rightBP: number;
  /** The Backbone Sequence instance being rendered */
  sequence: Sequence;
}

export default function CircularAxis({
  basePairToAngle,
  fullCircleView,
  layout,
  leftBP,
  rightBP,
  sequence,
}: CircularAxisProps): JSX.Element {
  const piecesData = sequence._sequence().finalized()
    ? null
    : asm
        .of(sequence._sequence())
        .getFragmentResultPieces(sequence._sequence())
        .map(([color, start, end]) => ({
          pieceStartAngle: basePairToAngle(start),
          pieceEndAngle: basePairToAngle(end),
          color,
        }));

  const sequenceLength = sequence.length();
  // The full set of ticks to display on the axis
  const ticks = getTicks({
    fullCircleView,
    leftBP,
    rightBP,
    sequenceLength,
  });
  const hasOne = ticks.includes(1);
  const tickSize = layout.get('tickSize');
  const {useBaseWidthStripe} = getBaseWidthStripeParams(
    sequenceLength,
    layout.get('outerRadius'),
    basePairToAngle
  );
  const pathArc = d3.lineRadial().curve(d3.curveBasis);
  const tickData: Array<{
    tick: number;
    path: string;
    startOffset: number;
    stroke: string;
    x1: number;
    x2: number;
    y1: number;
    y2: number;
  }> = ticks.map((tick) => {
    // Angle in radians between the sequence origin and this tick
    const standardAngle = basePairToAngle(sequtil.displayToModel(tick, sequence._sequence().startIndex()));
    // The amount of space to include on the curved text path on either side of the tick line
    const anglePadding = Math.min(
      layout.get('type').width(String(tick).length * 2) / layout.get('outerRadius'),
      Math.PI
    );

    // Builds the 'd' attribute of the path given a range of measurements in radians
    let pathArcRadius: number;
    let pathParts: Array<number>;
    if (Math.PI / 2 < standardAngle && standardAngle < (3 * Math.PI) / 2) {
      // Tick is drawn on the 'bottom' of the circle, and will need to be rendered 'upside down'
      pathParts = d3.range(standardAngle + anglePadding, standardAngle - anglePadding, -anglePadding / 4);
      pathArcRadius = layout.get('outerRadius') - tickSize;
    } else {
      pathParts = d3.range(standardAngle - anglePadding, standardAngle + anglePadding, anglePadding / 4);
      pathArcRadius = layout.get('outerRadius') - tickSize - layout.get('type').height() + 2;
    }
    pathArc.radius(pathArcRadius);
    // The 'd' attribute of the path on which the tick string is drawn;
    const path = pathArc(pathParts.map((angle) => [angle, pathArcRadius]))!;
    const stroke = getTickColor({
      fragmentResults: sequence._sequence().fragmentResults().models,
      piecesData,
      standardAngle,
      startIndex: sequence.startIndex(),
      tick,
      useBaseWidthStripe,
    });

    // The inner end of the tick line
    const point1 = Display.polar(
      tick === 1
        ? layout.get('outerRadius') - tickSize - layout.get('type').height()
        : layout.get('outerRadius') - tickSize,
      standardAngle
    );
    // The outer end of the tick line
    const point2 = Display.polar(layout.get('outerRadius'), standardAngle);
    return {
      tick,
      path,
      startOffset: pathArcRadius * anglePadding,
      stroke,
      x1: point1.x,
      x2: point2.x,
      y1: point1.y,
      y2: point2.y,
    };
  });

  return (
    <g className="axis-labels">
      {tickData.map(({tick, path, startOffset, stroke, x1, x2, y1, y2}) => (
        <g key={tick} className="axis-label">
          <text textAnchor="middle" fontWeight={900} fontSize={layout.get('type').fontSize}>
            <textPath className="text-path" xlinkHref={`#axis-${tick}`} startOffset={startOffset}>
              {tick === 1 && !hasOne ? '' : tick}
            </textPath>
          </text>
          <path stroke="none" fill="none" d={path} id={`axis-${tick}`} />
          <line
            stroke={stroke}
            strokeWidth={tick === 1 ? 4 : 2}
            shapeRendering="geometricPrecision"
            x1={x1}
            x2={x2}
            y1={y1}
            y2={y2}
          />
        </g>
      ))}
    </g>
  );
}
